import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useMyProperties } from "@/lib/hooks";
import { PageHeader, Empty, AuthImage } from "@/components/common";
import { PropertySelect } from "@/components/FormBits";
import { PhotoUpload } from "@/components/PhotoUpload";
import { Field } from "@/components/PropertyDialog";
import { tw } from "@/lib/i18n_extra";
import { SignDialog } from "@/components/SignaturePad";
import { DateInput } from "@/components/DateInput";

export const DOC_CATS = { bail: ["Bail", "Lease"], mandat: ["Mandat de gestion", "Management mandate"], etat_des_lieux: ["État des lieux", "Inventory report"], diagnostic: ["Diagnostic", "Survey"], assurance: ["Attestation d'assurance", "Insurance certificate"], autre: ["Autre", "Other"] };
const STATES = { Neuf: "New", Bon: "Good", "Usé": "Worn", "Dégradé": "Damaged" };
const stateName = (s, lang) => tw([s, STATES[s] || s], lang);
const EMPTY = { property_id: "", category: "bail", title: "", lease_id: "", files: [], data: {} };

function InventoryRooms({ rooms, onChange }) {
  const { t, lang } = usePrefs();
  const set = (i, k, v) => onChange(rooms.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid="inventory-rooms">
      {rooms.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_110px_1fr_20px] gap-2">
          <Input className="h-9" placeholder={t("roomName")} value={r.name} onChange={(e) => set(i, "name", e.target.value)} data-testid={`room-name-${i}`} />
          <Select value={r.state} onValueChange={(v) => set(i, "state", v)}><SelectTrigger className="h-9" data-testid={`room-state-${i}`}><SelectValue /></SelectTrigger><SelectContent>{Object.keys(STATES).map((s) => <SelectItem key={s} value={s}>{stateName(s, lang)}</SelectItem>)}</SelectContent></Select>
          <Input className="h-9" placeholder="Commentaire" value={r.comment} onChange={(e) => set(i, "comment", e.target.value)} />
          <button type="button" onClick={() => onChange(rooms.filter((_, j) => j !== i))} className="text-slate-400"><X size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rooms, { name: "", state: "Bon", comment: "" }])} className="text-xs font-semibold text-[#996515]" data-testid="add-room-button">+ {t("roomsState")}</button>
    </div>
  );
}

function DocumentDialog() {
  const { t, lang } = usePrefs();
  const qc = useQueryClient();
  const { data: leases = [] } = useQuery({ queryKey: ["leases"], queryFn: () => fetcher("/leases") });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const setData = (k, v) => setF({ ...f, data: { ...f.data, [k]: v } });
  const save = async () => {
    try { await api.post("/documents", { ...f, lease_id: f.lease_id || null }); qc.invalidateQueries({ queryKey: ["documents"] }); setOpen(false); setF(EMPTY); } catch (e) { toast.error(errMsg(e)); }
  };
  const pick = (v) => setF({ ...f, category: v, data: v === "etat_des_lieux" ? { kind: "entree", rooms: [{ name: "Séjour", state: "Bon", comment: "" }] } : {} });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="new-document-button"><Plus size={16} className="mr-1" />{t("newDocument")}</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-sm" data-testid="document-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("newDocument")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("category")}>
            <Select value={f.category} onValueChange={pick}><SelectTrigger data-testid="document-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(DOC_CATS).map(([k, v]) => <SelectItem key={k} value={k}>{tw(v, lang)}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("property")}><PropertySelect value={f.property_id} onChange={(v) => setF({ ...f, property_id: v, lease_id: "" })} testId="document-property-select" /></Field>
          <div className="col-span-2"><Field label={t("title")}><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="document-title-input" /></Field></div>
          <div className="col-span-2">
            <Field label={`${t("lease")} (${t("tenant").toLowerCase()})`}>
              <Select value={f.lease_id} onValueChange={(v) => setF({ ...f, lease_id: v })}><SelectTrigger data-testid="document-lease-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{leases.filter((l) => l.property_id === f.property_id).map((l) => <SelectItem key={l.id} value={l.id}>{l.tenant?.name} · {l.unit?.reference}</SelectItem>)}</SelectContent></Select>
            </Field>
          </div>
          {f.category === "mandat" && <>
            <Field label={t("owners")}><Input value={f.data.owner_name || ""} onChange={(e) => setData("owner_name", e.target.value)} data-testid="mandate-owner-input" /></Field>
            <Field label={t("feeRate")}><Input type="number" value={f.data.fee_rate || ""} onChange={(e) => setData("fee_rate", Number(e.target.value))} data-testid="mandate-fee-input" /></Field>
            <Field label={t("start")}><DateInput value={f.data.start || ""} onChange={(e) => setData("start", e.target.value)} data-testid="mandate-start-input" /></Field>
            <Field label={t("end")}><DateInput value={f.data.end || ""} onChange={(e) => setData("end", e.target.value)} data-testid="mandate-end-input" /></Field>
          </>}
          {f.category === "etat_des_lieux" && <>
            <Field label={t("type")}>
              <Select value={f.data.kind} onValueChange={(v) => setData("kind", v)}><SelectTrigger data-testid="inventory-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="entree">{t("entry")}</SelectItem><SelectItem value="sortie">{t("exit")}</SelectItem></SelectContent></Select>
            </Field>
            <div className="col-span-2"><Field label={t("roomsState")}><InventoryRooms rooms={f.data.rooms} onChange={(r) => setData("rooms", r)} /></Field></div>
          </>}
        </div>
        <Field label={t("files")}><PhotoUpload value={f.files} onChange={(v) => setF({ ...f, files: v })} testId="document-file-upload" /></Field>
        <Button onClick={save} disabled={!f.property_id || !f.title} className="rounded-sm bg-[#0F172A]" data-testid="document-save-button">{t("create")}</Button>
      </DialogContent>
    </Dialog>
  );
}

function Signatures({ d }) {
  const { user } = useAuth();
  const { t, fdatetime } = usePrefs();
  const sigs = d.signatures || [];
  const mine = sigs.some((s) => s.user_id === user.id);
  return (
    <div className="mt-4 border-t border-slate-100 pt-3" data-testid={`signatures-${d.id}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t("signatures")} ({sigs.length})</span>
        {mine ? <span className="text-xs font-semibold text-emerald-700" data-testid={`signed-badge-${d.id}`}>✓ {t("signed")}</span> : <SignDialog doc={d} />}
      </div>
      <div className="flex flex-wrap gap-3">
        {sigs.map((s) => (
          <div key={s.user_id} className="w-36 rounded-sm border border-slate-200 p-1.5" data-testid={`signature-${d.id}-${s.user_id}`}>
            <AuthImage path={s.image_path} className="h-12 w-full object-contain" />
            <div className="mt-1 truncate text-[11px] font-semibold">{s.name}</div>
            <div className="text-[10px] text-slate-500">{t(`role_${s.role}`)} · {fdatetime(s.signed_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocCard({ d, staff, propName, onDelete }) {
  const { t, lang, fdate } = usePrefs();
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid={`document-card-${d.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div><div className="eyebrow">{tw(DOC_CATS[d.category], lang)}{d.data?.kind ? ` · ${t(d.data.kind === "entree" ? "entry" : "exit")}` : ""}</div><h3 className="mt-1 font-display text-lg font-bold">{d.title}</h3><div className="text-xs text-slate-500">{propName} · {d.created_by} · {fdate(d.created_at)}</div></div>
        {staff && <button onClick={() => onDelete(d.id)} className="text-slate-500 hover:text-rose-600" data-testid={`document-delete-${d.id}`}><Trash2 size={14} /></button>}
      </div>
      {d.category === "mandat" && <div className="mt-3 text-sm text-slate-700">{d.data.owner_name} · {t("feeRate")} <span className="font-data">{d.data.fee_rate}%</span> · {fdate(d.data.start)} → {fdate(d.data.end)}</div>}
      {d.data?.rooms && <div className="mt-3 space-y-1 text-sm">{d.data.rooms.map((r, i) => <div key={i} className="flex justify-between border-b border-slate-100 py-1"><span>{r.name}</span><span className="text-xs text-slate-500">{stateName(r.state, lang)}{r.comment ? ` — ${r.comment}` : ""}</span></div>)}</div>}
      {d.files?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{d.files.map((p) => p.endsWith(".pdf") ? <span key={p} className="rounded-sm border px-2 py-1 text-xs">PDF</span> : <AuthImage key={p} path={p} className="h-16 w-20 rounded-sm object-cover" />)}</div>}
      <Signatures d={d} />
    </div>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const { t, lang } = usePrefs();
  const qc = useQueryClient();
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  const names = Object.fromEntries(useMyProperties(user.role !== "locataire").map((p) => [p.id, p.name]));
  const { data = [] } = useQuery({ queryKey: ["documents"], queryFn: () => fetcher("/documents") });
  const [cat, setCat] = useState("");
  const del = async (id) => { try { await api.delete(`/documents/${id}`); qc.invalidateQueries({ queryKey: ["documents"] }); } catch (e) { toast.error(errMsg(e)); } };
  const shown = data.filter((d) => !cat || d.category === cat);
  return (
    <div data-testid="documents-page">
      <PageHeader eyebrow={t("secAsset")} title={t("docs")}>{staff && <DocumentDialog />}</PageHeader>
      <div className="mb-5 flex flex-wrap gap-1.5">
        {["", ...Object.keys(DOC_CATS)].map((k) => (
          <button key={k || "all"} onClick={() => setCat(k)} data-testid={`document-filter-${k || "all"}`} className={`rounded-sm border px-3 py-1.5 text-xs font-semibold ${cat === k ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{k ? tw(DOC_CATS[k], lang) : t("all")}</button>
        ))}
      </div>
      {shown.length === 0 && <Empty />}
      <div className="grid gap-4 md:grid-cols-2">{shown.map((d) => <DocCard key={d.id} d={d} staff={staff} propName={names[d.property_id] || d.property_name} onDelete={del} />)}</div>
    </div>
  );
}
